import { FC, useEffect, useMemo, useReducer, useState } from 'react';

import {
  Button,
  Col,
  Empty,
  Flex,
  Form,
  FormInstance,
  SelectProps,
  Spin,
  Typography,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
  ResponsiveContainer,
  Line,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import { plantApi } from '@/api';
import SAMPLE_GENERATION_TEMPLATE from '@/assets/files/plant_generation_template.xlsx';
import IconDownload from '@/assets/icons/icon-download.svg?react';
import IconError from '@/assets/icons/icon-error.svg?react';
import IconInbox from '@/assets/icons/icon-inbox.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import IconWarning from '@/assets/icons/icon-warning.svg?react';
import GenerationImageSRC from '@/assets/images/generation-image.png';
import Card from '@/components/RCard';
import RChartTooltip from '@/components/RGraphicTooltip';
import RSelect from '@/components/RSelect';
import RToggleButtons from '@/components/RToggleButtons';
import RTooltip from '@/components/RTooltip';
import { FILE_FORMATS } from '@/constants';
import CustomAxiosError from '@/types/error';
import { Draft } from '@/types/global';
import {
  AnnualGenerationGraph,
  DailyGenerationGraph,
  DailyGenerationGraphData,
  Plant,
  PlantForm,
} from '@/types/plant';
import {
  camelCaseToNormalCase,
  formatNumberWithCommas,
  getYearsInRangeOptions,
} from '@/utils';

import { filterReducer, initialState } from './filters/reducer';
import { FilterState } from './filters/types';
import StyledGenerationProfile from './styles';

type Props = {
  disabled?: boolean;
  form: FormInstance<PlantForm>;
  currentPlantData?: Plant;
  draft?: Draft;
  setDraft?: React.Dispatch<React.SetStateAction<Draft>>;
  uploadError?: string | null;
  setUploadError?: React.Dispatch<React.SetStateAction<string | null>>;
};

const GenerationProfile: FC<Props> = (props) => {
  const {
    disabled = false,
    form,
    currentPlantData,
    draft,
    setDraft,
    uploadError,
    setUploadError,
  } = props;
  const { t } = useTranslation();
  const draftTyped = draft as Draft;
  const setDraftTyped = setDraft as React.Dispatch<React.SetStateAction<Draft>>;
  const uploadErrorTyped = uploadError as string | null;
  const setUploadErrorTyped = setUploadError as React.Dispatch<
    React.SetStateAction<string | null>
  >;
  const theme = useTheme();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const generationFile = form.getFieldValue('generation');

  const [currentPlantId, setCurrentPlantId] = useState<
    string | undefined | number
  >();

  useEffect(() => {
    if (generationFile) {
      if (
        typeof currentPlantData?.generation === 'string' &&
        typeof generationFile === 'string'
      ) {
        const existingFile: UploadFile = {
          uid: '-1',
          name: t('plants.form.generationProfile.currentGenerationFile'),
          status: 'done',
          thumbUrl: GenerationImageSRC,
        };
        setFileList([existingFile]);
      }
    }
  }, [generationFile, currentPlantData, t]);

  const [filterParams, dispatch] = useReducer(filterReducer, initialState);

  useEffect(() => {
    const id = draftTyped?.draftId || currentPlantData?.id;
    setCurrentPlantId(id);
  }, [currentPlantData, draftTyped]);

  const { apiEndpoint, queryString } = useMemo((): {
    apiEndpoint: string | null;
    queryString: string | null;
  } => {
    if (currentPlantId && filterParams.selectedYear) {
      const endpoint =
        filterParams.activeFilter === 'daily'
          ? `/plant/${currentPlantId}/generation_graph/daily/`
          : filterParams.activeFilter === 'annual'
            ? `/plant/${currentPlantId}/generation_graph/`
            : '';

      const params: Record<string, string> = {};

      if (filterParams.activeFilter === 'annual') {
        if (filterParams.selectedYear) {
          params.startDatetime = dayjs(
            `${filterParams.selectedYear}-01-01`
          ).toISOString();
          params.endDatetime = dayjs(
            `${filterParams.selectedYear}-12-31`
          ).toISOString();
          params.timeUnit = `month`;
        }
      }

      if (filterParams.activeFilter === 'daily') {
        const { dayOfWeek, season } = filterParams.daily;

        if (filterParams.selectedYear) {
          params.year = filterParams.selectedYear.toString();
        }

        if (dayOfWeek && dayOfWeek !== 'all') {
          params.dayOfWeek = dayOfWeek;
        }

        if (season && season !== 'all') {
          params.season = season;
        }
      }

      const queryString = new URLSearchParams(params).toString();
      return {
        apiEndpoint: endpoint,
        queryString,
      };
    }

    return {
      apiEndpoint: null,
      queryString: null,
    };
  }, [filterParams, currentPlantId]);

  const [fullURL, setFullURL] = useState(() => {
    return apiEndpoint ? `${apiEndpoint}?${queryString}` : null;
  });

  useEffect(() => {
    if (apiEndpoint) {
      setFullURL(`${apiEndpoint}?${queryString}`);
    } else {
      setFullURL(null);
    }
  }, [apiEndpoint, queryString]);

  const {
    data: generatedGraphData,
    isLoading: isGraphDataLoading,
    error,
  } = useSWR<DailyGenerationGraph | AnnualGenerationGraph>(fullURL);

  const getThumbnail = (file: UploadFile) => {
    if (file.status === 'error') {
      return;
    }
    return GenerationImageSRC;
  };

  const handleFileChange: UploadProps['onChange'] = (props) => {
    const { fileList } = props;

    const extendedFileList = fileList.map((file) => {
      if (file.response) {
        file.url = file.response.url;
      }
      file.thumbUrl = getThumbnail(file);
      return file;
    });

    setFileList(extendedFileList);
  };

  // TODO:(Fix This Later) Reset Handler ında Draft ı resetlememiz gerekiyor. Handle Remove handlerında draftı resetlememize gerek yok.
  const handleRemove: UploadProps['onRemove'] = () => {
    setUploadErrorTyped(null);
    setCurrentPlantId(undefined);
  };

  const customFileUploadRequest: UploadProps['customRequest'] = async (
    options
  ) => {
    const { file, onSuccess, onError, onProgress } = options;
    try {
      setFullURL(null);

      const installedPowerKw = form.getFieldValue('installedPowerKw');
      const licensedPowerKw = form.getFieldValue('licensedPowerKw');
      const generationType = form.getFieldValue('generationType');

      const response = await plantApi.postDraftPlantGenerationFile({
        file: file,
        installedPowerKw: installedPowerKw,
        licensedPowerKw: licensedPowerKw,
        generationType: generationType,
      });

      //TODO Check this later if it is needed
      await plantApi.postDraftPlantGenerationFile(
        {
          file: file,
          draftId: response.draftId,
          installedPowerKw: installedPowerKw,
          licensedPowerKw: licensedPowerKw,
          generationType: generationType,
        },
        (progress) => {
          onProgress && onProgress(progress);
        }
      );

      setDraftTyped({
        draftId: response.draftId,
        startDate: response.startDate,
        endDate: response.endDate,
      });
      onSuccess && onSuccess(response);
    } catch (error) {
      const axiosError = error as CustomAxiosError;
      const errorMessage =
        axiosError.response?.data.userMessage ||
        t('plants.form.generationProfile.defaultErrorMessage');

      setUploadErrorTyped(errorMessage);
      onError && onError(error as Error);
    }
  };

  const handleFilterChange = (
    key: keyof Partial<FilterState['daily']> | 'selectedYear',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  ) => {
    if (key === 'selectedYear') {
      dispatch({
        type: 'SET_SELECTED_YEAR',
        payload: value,
      });
    } else if (
      filterParams.activeFilter === 'daily' &&
      key in filterParams.daily
    ) {
      dispatch({
        type: 'SET_DAILY_FILTER',
        payload: {
          [key]: value,
        },
      });
    }
  };

  const handleActiveFilterChange = (value: string) => {
    if (value === t('plants.form.generationProfile.filters.daily')) {
      dispatch({
        type: 'SET_ACTIVE_FILTER',
        payload: 'daily',
      });
    } else if (value === t('plants.form.generationProfile.filters.annually')) {
      dispatch({
        type: 'SET_ACTIVE_FILTER',
        payload: 'annual',
      });
    }
  };

  const daysOfWeekOptions: SelectProps['options'] = [
    {
      label: t('plants.form.generationProfile.daysOfWeek.monday'),
      value: 'monday',
    },
    {
      label: t('plants.form.generationProfile.daysOfWeek.tuesday'),
      value: 'tuesday',
    },
    {
      label: t('plants.form.generationProfile.daysOfWeek.wednesday'),
      value: 'wednesday',
    },
    {
      label: t('plants.form.generationProfile.daysOfWeek.thursday'),
      value: 'thursday',
    },
    {
      label: t('plants.form.generationProfile.daysOfWeek.friday'),
      value: 'friday',
    },
    {
      label: t('plants.form.generationProfile.daysOfWeek.saturday'),
      value: 'saturday',
    },
    {
      label: t('plants.form.generationProfile.daysOfWeek.sunday'),
      value: 'sunday',
    },
    {
      label: t('plants.form.generationProfile.daysOfWeek.allDays'),
      value: 'all',
    },
  ];

  const seasonOptions: SelectProps['options'] = [
    {
      label: t('plants.form.generationProfile.seasons.spring'),
      value: 'spring',
    },
    {
      label: t('plants.form.generationProfile.seasons.summer'),
      value: 'summer',
    },
    {
      label: t('plants.form.generationProfile.seasons.autumn'),
      value: 'autumn',
    },
    {
      label: t('plants.form.generationProfile.seasons.winter'),
      value: 'winter',
    },
    {
      label: t('plants.form.generationProfile.seasons.allSeasons'),
      value: 'all',
    },
  ];

  useEffect(() => {
    const startDate = draftTyped.startDate || currentPlantData?.startDate;
    const endDate = draftTyped.endDate || currentPlantData?.endDate;

    if (startDate && endDate) {
      const years = getYearsInRangeOptions(startDate, endDate);
      dispatch({
        type: 'SET_YEARS',
        payload: years,
      });
    }
  }, [draftTyped, currentPlantData]);

  const transformDailyData = (
    data: DailyGenerationGraphData
  ): { [key: string]: number | string }[] => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    return hours.map((hour) => {
      const formattedHour = `${String(hour).padStart(2, '0')}:00`;
      const hourData: { [key: string]: number | string } = {
        hour: formattedHour,
        average: data.avg.generation[hour],
      };
      data.all.forEach((dayData, index) => {
        hourData[`day${index + 1}`] = dayData.generation[hour];
      });
      return hourData;
    });
  };

  const isDailyGraphData = (
    data: DailyGenerationGraph | AnnualGenerationGraph
  ): data is DailyGenerationGraph => {
    return (
      (data as DailyGenerationGraph).graphData !== undefined &&
      Array.isArray((data as DailyGenerationGraph).graphData.all)
    );
  };

  const transformedDailyData =
    filterParams.activeFilter === 'daily' &&
    generatedGraphData &&
    isDailyGraphData(generatedGraphData)
      ? transformDailyData(generatedGraphData.graphData)
      : [];

  return (
    <StyledGenerationProfile
      wrapperCol={{ span: 24 }}
      className="p-16 pb-40"
      style={{
        border: '0.5px solid red',
        borderRadius: '10px',
        borderColor: theme.colors.grayLightAccent,
      }}
    >
      {!currentPlantData?.generation ? (
        <a
          style={{ color: theme.colors.bluePrimary }}
          href={SAMPLE_GENERATION_TEMPLATE}
        >
          <IconDownload fill={theme.colors.bluePrimary} />
          &nbsp; {t('plants.form.generationProfile.downloadTemplate')}
        </a>
      ) : (
        <a style={{ color: theme.colors.bluePrimary }} href={generationFile}>
          <IconDownload fill={theme.colors.bluePrimary} />
          &nbsp; {t('plants.form.generationProfile.downloadCurrentProfile')}
        </a>
      )}
      {!disabled && (
        <>
          <Form.Item<PlantForm>
            label={t('plants.form.generationProfile.uploadLabel')}
            name="generation"
            className="mt-16 mb-0"
            rules={[
              {
                required: true,
                message: t('plants.form.generationProfile.requiredMessage'),
              },
            ]}
            getValueFromEvent={({ fileList }) => fileList[0]?.originFileObj}
          >
            <Upload
              disabled={disabled}
              onRemove={handleRemove}
              customRequest={customFileUploadRequest}
              accept={`${FILE_FORMATS.csv}, ${FILE_FORMATS.xlsx}`}
              multiple={false}
              fileList={fileList}
              listType="picture"
              onChange={handleFileChange}
            >
              {fileList.length == 1 ? null : (
                <Empty
                  image={<IconInbox fill={theme.colors.primary} />}
                  styles={{ image: { height: '100%' } }}
                  description={
                    <Button type="link">
                      {t('plants.form.generationProfile.uploadDescription')}
                    </Button>
                  }
                ></Empty>
              )}
            </Upload>
          </Form.Item>
          {uploadErrorTyped && (
            <Form.Item
              className="upload-error"
              help={uploadErrorTyped}
              validateStatus="error"
            />
          )}
        </>
      )}

      <Col span={24}>
        <Flex
          align="center"
          justify="space-between"
          gap={24}
          wrap
          className="capacity-factor-container"
        >
          {isGraphDataLoading ? (
            <Flex justify="center" align="center" className="w-100">
              <Spin style={{ margin: '35px' }} />
            </Flex>
          ) : generatedGraphData ? (
            <>
              <div className="capacity-factor-stat">
                <Typography.Text className="fs-16-regular text-gray-color mb-6">
                  {t('plants.form.generationProfile.capacityFactorMechanic')}
                </Typography.Text>
                <Typography.Text className="fs-22-bold text-primary-color">
                  %{' '}
                  {Number(generatedGraphData?.capacityFactorMechanic).toFixed(
                    2
                  )}
                </Typography.Text>
              </div>
              <div className="capacity-factor-stat">
                <Typography.Text className="fs-16-regular text-gray-color mb-6">
                  {t('plants.form.generationProfile.capacityFactorElectric')}
                </Typography.Text>
                <Typography.Text className="fs-22-bold text-primary-color">
                  %{' '}
                  {Number(generatedGraphData?.capacityFactorElectric).toFixed(
                    2
                  )}
                </Typography.Text>
              </div>
              <div className="capacity-factor-stat">
                <Typography.Text className="fs-16-regular text-gray-color mb-6">
                  {t('plants.form.generationProfile.curtailmentPercentage')}
                </Typography.Text>
                <Typography.Text className="fs-22-bold text-primary-color">
                  %{' '}
                  {Number(generatedGraphData?.curtailmentPercentage).toFixed(2)}
                </Typography.Text>
              </div>
            </>
          ) : (
            <Flex justify="center" align="center" className="w-100">
              <Typography.Text className="fs-16-regular text-gray-color">
                {t('plants.form.generationProfile.uploadToViewData')}
              </Typography.Text>
            </Flex>
          )}
        </Flex>
      </Col>

      <Col span={24}>
        <Card
          $minHeight="300px"
          style={{ width: '100%', marginTop: '24px' }}
          $paddingBody="24px 0px"
          title={
            <Flex gap={8} align="center">
              <Typography.Title level={4} className="fs-17-bold">
                {t('plants.form.generationProfile.generationTitle')}
              </Typography.Title>
              <RTooltip
                title={t('plants.form.generationProfile.generationTitle')}
                description={t(
                  'plants.form.generationProfile.generationTooltip'
                )}
              >
                <IconInfo />
              </RTooltip>
            </Flex>
          }
          extra={
            filterParams.years.length > 0 && (
              <Flex gap={16} align="center">
                <Flex style={{ height: '32px' }} gap={8} align="center">
                  <RToggleButtons
                    disabled={isGraphDataLoading}
                    height={'auto'}
                    labels={[
                      t('plants.form.generationProfile.filters.daily'),
                      t('plants.form.generationProfile.filters.annually'),
                    ]}
                    onLabelChange={handleActiveFilterChange}
                  />
                  <RSelect
                    disabled={isGraphDataLoading}
                    style={{ height: '100%' }}
                    width={120}
                    defaultValue={filterParams.selectedYear}
                    options={filterParams.years.map((year) => ({
                      label: year.toString(),
                      value: year,
                    }))}
                    onChange={(value) =>
                      handleFilterChange('selectedYear', value)
                    }
                  />
                  {filterParams.activeFilter === 'daily' && (
                    <>
                      <RSelect
                        disabled={isGraphDataLoading}
                        style={{ height: '100%' }}
                        width={120}
                        defaultValue={filterParams.daily.dayOfWeek}
                        options={daysOfWeekOptions}
                        onChange={(value) =>
                          handleFilterChange('dayOfWeek', value)
                        }
                      />
                      <RSelect
                        disabled={isGraphDataLoading}
                        style={{ height: '100%' }}
                        width={120}
                        defaultValue={filterParams.daily.season}
                        options={seasonOptions}
                        onChange={(value) =>
                          handleFilterChange('season', value)
                        }
                      />
                    </>
                  )}
                </Flex>
                <Button
                  icon={<IconDownload fill={theme.colors.bluePrimary} />}
                />
              </Flex>
            )
          }
        >
          {isGraphDataLoading && (
            <Flex justify="center" align="center" style={{ height: 400 }}>
              <Spin style={{ margin: '20px' }} />
            </Flex>
          )}
          {!isGraphDataLoading && generatedGraphData && error && (
            <Flex
              vertical
              gap={12}
              justify="center"
              align="center"
              style={{ height: 400 }}
            >
              <IconError
                width={50}
                height={50}
                fill={theme.colors.tagFailure}
              />
              {error && (
                <Typography.Text className="fs-14-regular text-gray-color text-center">
                  {t('plants.form.generationProfile.errorMessage')}
                </Typography.Text>
              )}
              {!generatedGraphData && (
                <Typography.Text className="fs-14-regular text-gray-color text-center">
                  {t('plants.form.generationProfile.noDataMessage')}
                </Typography.Text>
              )}
            </Flex>
          )}
          {!isGraphDataLoading && !generatedGraphData && (
            <Flex
              vertical
              style={{ height: 300 }}
              justify="center"
              align="center"
            >
              <IconWarning
                fill={theme.colors.tagRevoked}
                className="mb-24"
                width={50}
                height={50}
              />
              <Typography.Text className="fs-14-regular text-gray-color text-center">
                {t('plants.form.generationProfile.uploadToViewGraph')}
              </Typography.Text>
            </Flex>
          )}
          {!isGraphDataLoading && generatedGraphData && (
            <>
              {isDailyGraphData(generatedGraphData) ? (
                // Render daily generation graph
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart
                    margin={{ top: 15, right: 24, left: 12, bottom: 15 }}
                    data={transformedDailyData}
                  >
                    <XAxis
                      dataKey="hour"
                      tickFormatter={(tick) => tick}
                      angle={-45}
                      textAnchor="end"
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      // domain={['auto', (dataMax: number) => dataMax * 1.1]}
                      domain={['auto', 'dataMax']}
                      allowDataOverflow={true}
                      // padding={{ top: 50, bottom: 0 }}
                      tickFormatter={(value) => `${value} MWh`}
                      interval="equidistantPreserveStart"
                    />
                    <CartesianGrid
                      horizontal={true}
                      vertical={false}
                      stroke={theme.colors.grayVeryLight}
                    />
                    {transformedDailyData.length > 0 &&
                      Object.keys(transformedDailyData[0])
                        .filter((key) => key !== 'hour')
                        .map((key) => (
                          <Line
                            dot={false}
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={theme.colors.grayVeryLight}
                            strokeWidth={0.5}
                          />
                        ))}
                    <Line
                      dot={false}
                      type="monotone"
                      dataKey="average"
                      stroke={theme.colors.greenPrimary}
                      strokeWidth={4}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                // Render Annual Generation Graph
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart
                    margin={{ top: 15, right: 24, left: 0, bottom: 15 }}
                    data={generatedGraphData.graphData}
                  >
                    <XAxis
                      angle={-45}
                      textAnchor="end"
                      interval="preserveStart"
                      tickFormatter={(tick) => dayjs(tick).format('MMM')}
                      dataKey="dt"
                    />
                    <YAxis
                      tickFormatter={(value) =>
                        `${formatNumberWithCommas(Number(value))}`
                      }
                      interval="preserveEnd"
                      dataKey="totalGeneration"
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        const typedPayload = payload as Payload<
                          number,
                          string
                        >[];

                        const transformedPayload = typedPayload.map(
                          (entry) => ({
                            ...entry,
                            name:
                              entry.name && camelCaseToNormalCase(entry.name),
                            unit: 'MWh',
                            color: theme.colors.primary,
                          })
                        );
                        return (
                          <RChartTooltip
                            active={active}
                            payload={transformedPayload}
                            label={`${dayjs(label).format('MMMM')}, ${dayjs(
                              label
                            ).format('YYYY')}`}
                          />
                        );
                      }}
                    />
                    <CartesianGrid
                      horizontal={true}
                      vertical={false}
                      stroke={theme.colors.grayVeryLight}
                    />
                    <Line
                      dot={false}
                      type="natural"
                      dataKey="totalGeneration"
                      stroke={theme.colors.primary}
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </>
          )}
        </Card>
      </Col>
    </StyledGenerationProfile>
  );
};

export default GenerationProfile;
